// @flow

import * as React from 'react';
import { useRelayEnvironment, QueryRenderer } from 'react-relay';
import type { RenderProps } from 'react-relay';
import { graphql, useFragment, useMutation } from 'react-relay';
import { Flex, Box } from 'react-system';
import { useFormik, Form } from '@realadvisor/form';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { isEmail } from '../../utils/isEmail';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { PhoneInput, usePhone } from '../../controls/phone-input';
import { useAnalytics } from '../../controls/analytics';
import { LoadingButton } from '../../controls/loading-button';
import { InlineMarked } from '../../controls/marked.js';
import { nth } from './utils/nth';
import type { DevelopmentEnquiryMeQueryResponse } from './__generated__/DevelopmentEnquiryMeQuery.graphql';
import type { DevelopmentEnquiryFormUpsertMutation } from './__generated__/DevelopmentEnquiryFormUpsertMutation.graphql';
import type { DevelopmentEnquiry_development$key } from './__generated__/DevelopmentEnquiry_development.graphql';

type Props = {|
  development: DevelopmentEnquiry_development$key,
  sentActions?: React.Node,
  selectedProductId?: string,
  showSubmit?: boolean,
  formRef?: {| current: null | { submitForm: () => void, ... } |},
  onProductSelect?: (id: string) => void,
  onSuccessfulSubmit?: () => void,
|};

const isEmpty = value =>
  value == null || (typeof value === 'string' && value.trim().length === 0);

const BaseDevelopmentEnquiry = props => {
  const development = useFragment(
    graphql`
      fragment DevelopmentEnquiry_development on DevelopmentProduct {
        id
        products {
          id
          lotReference
          salePrice
          currency
          status
          property {
            numberOfRooms
            floorOfFlat
          }
        }
      }
    `,
    props.development,
  );

  const { t, numberLocale, currency } = useLocale();
  const { track } = useAnalytics();
  const { text } = useTheme();
  const [createEnquiry, enquiryCreating] =
    useMutation<DevelopmentEnquiryFormUpsertMutation>(
      graphql`
        mutation DevelopmentEnquiryFormUpsertMutation(
          $input: CreateEnquiryInput!
        ) {
          createEnquiry(input: $input) {
            enquiry {
              id
            }
            errors {
              productId
            }
          }
        }
      `,
    );
  const [enquirySent, setEnquirySent] = React.useState(false);
  const { me, selectedProductId, showSubmit = true } = props;

  const availableProducts = (development.products ?? []).filter(
    p => p.status === 'available',
  );

  const { initialPhonePrefix, validatePhone } = usePhone();

  const initialValues = {
    productId: selectedProductId,
    firstName: me?.firstName || '',
    lastName: me?.lastName || '',
    emailAddress: me?.primaryEmail?.email || '',
    phoneNumber: me?.primaryPhoneNumber?.number || initialPhonePrefix,
    propertySearchNotification: 'daily',
  };

  const formik = useFormik({
    initialValues,
    validate: values => {
      const errors = {};
      if (isEmpty(values.emailAddress)) {
        errors.emailAddress = t('emailRequired');
      } else if (!isEmail(values.emailAddress)) {
        errors.emailAddress = t('invalidEmail');
      }
      if (isEmpty(values.firstName)) {
        errors.firstName = t('firstNameRequired');
      }
      if (isEmpty(values.lastName)) {
        errors.lastName = t('lastNameRequired');
      }
      if (isEmpty(values.productId)) {
        errors.productId = t('productRequired');
      }
      if (isEmpty(values.phoneNumber)) {
        errors.phoneNumber = t('phoneNumberRequired');
      } else {
        errors.phoneNumber = validatePhone(values.phoneNumber);
      }
      return errors;
    },
    onSubmit: values => {
      createEnquiry({
        variables: {
          input: {
            enquiry: {
              productId: values.productId != null ? values.productId : '',
              firstName: values.firstName,
              lastName: values.lastName,
              emailAddress: values.emailAddress,
              phoneNumber: values.phoneNumber ? values.phoneNumber : undefined,
              portalId:
                'UG9ydGFsOjUxYTBkMzdhLTgwZjUtMTFlOC1iZWRmLTE3OTFmMWE0YmUzZg==',
              source: 'portal',
              propertySearchNotification: values.propertySearchNotification,
            },
          },
        },
        onCompleted: data => {
          track('RA.Web.Development.Contact.Click.Success', {
            developmentId: development.id ?? null,
            productId: values.productId ?? null,
          });

          if (data.createEnquiry?.errors != null) {
            if (data.createEnquiry.errors.productId != null) {
              formik.setResponseErrors({
                productId: t('developmentProductDuplicateError'),
              });
            }

            return;
          }

          setEnquirySent(true);
        },
      });
    },
  });

  React.useImperativeHandle(props.formRef, () => formik);

  const { values, setValues, errors, setTouched } = formik;

  return (
    <>
      {enquirySent ? (
        <>
          <EnquirySent />
          {props.sentActions}
        </>
      ) : (
        <Form onSubmit={formik.submitForm}>
          <Flex flexWrap="wrap" mx={-2}>
            <Box width={1} p={2}>
              <TextField
                select={true}
                label={
                  <InlineMarked>
                    {t('whichLotAreYouInterestedAbout')}
                  </InlineMarked>
                }
                variant="filled"
                error={errors.productId != null}
                helperText={errors.productId ?? null}
                FormHelperTextProps={{
                  variant: 'standard',
                }}
                value={values.productId || ''}
                onChange={evt => {
                  setValues({ productId: evt.target.value });
                  if (props.onProductSelect != null) {
                    props.onProductSelect(evt.target.value);
                  }
                }}
              >
                {availableProducts.map(product => {
                  const numberOfRooms = product.property.numberOfRooms;
                  const floorOfFlat = product.property.floorOfFlat;

                  return (
                    <MenuItem value={product.id} key={product.id}>
                      {[
                        product.lotReference,
                        product.salePrice != null &&
                          numberLocale.format(
                            Math.round(product.salePrice || 0),
                            {
                              currency: product.currency ?? currency,
                              style: 'currency',
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            },
                          ),
                        numberOfRooms != null &&
                          `${numberOfRooms} ${t('numberOfRooms')}`,
                        floorOfFlat != null && getFloorKey(floorOfFlat, t),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>

            <Box css={{ minWidth: 220 }} flexGrow={1} p={2}>
              <TextField
                required={true}
                label={t('firstName')}
                placeholder={t('enterFirstName')}
                value={values.firstName}
                error={errors.firstName != null}
                helperText={errors.firstName ?? null}
                variant="filled"
                FormHelperTextProps={{
                  variant: 'standard',
                }}
                onChange={event =>
                  setValues({
                    firstName: event.target.value,
                  })
                }
                onBlur={() => setTouched({ firstName: true })}
              />
            </Box>

            <Box css={{ minWidth: 220 }} flexGrow={1} p={2}>
              <TextField
                required={true}
                label={t('lastName')}
                placeholder={t('enterLastName')}
                value={values.lastName}
                error={errors.lastName != null}
                helperText={errors.lastName ?? null}
                variant="filled"
                FormHelperTextProps={{
                  variant: 'standard',
                }}
                onChange={event =>
                  setValues({
                    lastName: event.target.value,
                  })
                }
                onBlur={() => setTouched({ lastName: true })}
              />
            </Box>

            <Box css={{ minWidth: 220 }} flexGrow={1} p={2}>
              <TextField
                required={true}
                label={t('emailAddress')}
                placeholder={t('email')}
                value={values.emailAddress}
                error={errors.emailAddress != null}
                helperText={errors.emailAddress ?? null}
                variant="filled"
                FormHelperTextProps={{
                  variant: 'standard',
                }}
                onChange={event =>
                  setValues({
                    emailAddress: event.target.value,
                  })
                }
                onBlur={() => setTouched({ emailAddress: true })}
              />
            </Box>

            <Box css={{ minWidth: 220 }} flexGrow={1} p={2}>
              <FormControl
                variant="filled"
                required={true}
                error={errors.phoneNumber != null}
              >
                <InputLabel>{t('phoneNumber')}</InputLabel>
                <PhoneInput
                  variant="filled"
                  value={values.phoneNumber}
                  onChange={value => setValues({ phoneNumber: value })}
                  onBlur={() => setTouched({ phoneNumber: true })}
                />
                {errors.phoneNumber != null && (
                  <FormHelperText variant="standard">
                    {errors.phoneNumber}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box css={{ minWidth: 220 }} flexGrow={1} p={2}>
              <FormControlLabel
                label={
                  <Box css={text.caption}>{t('enquiryNotifyMessage')}</Box>
                }
                control={
                  <Checkbox
                    checked={values.propertySearchNotification === 'daily'}
                    color="primary"
                    onChange={evt => {
                      setValues({
                        propertySearchNotification: evt.target.checked
                          ? 'daily'
                          : 'never',
                      });
                    }}
                  />
                }
              />
            </Box>

            {showSubmit && (
              <Box css={{ minWidth: 220 }} flexGrow={1} px={2} pt={2}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  disabled={enquiryCreating}
                  loading={enquiryCreating}
                  onClick={() => {
                    track('RA.Web.Development.Contact.Click', {
                      productId: values.productId ?? null,
                    });
                    formik.submitForm();
                  }}
                >
                  {t('requestMoreInfo')}
                </LoadingButton>
              </Box>
            )}
          </Flex>
        </Form>
      )}
    </>
  );
};

export const EnquirySent = (): React.Node => {
  const { t } = useLocale();
  const { text, colors } = useTheme();
  return (
    <Paper
      css={{
        background: colors.green[100],
        border: `1px solid ${colors.green[200]}`,
        boxShadow: 'none',
        padding: '16px',
        color: colors.green[900],
        minWidth: 220,
        textAlign: 'center',
      }}
    >
      <Box css={text.subtitle1}>{t('enquirySent')}</Box>
    </Paper>
  );
};

const getFloorKey = (floorOfFlat, t) => {
  if (floorOfFlat === 0) {
    return t('ground');
  }
  return floorOfFlat != null ? nth(floorOfFlat, t) : '-';
};

export const DevelopmentEnquiry = (props: Props): React.Node => {
  const environment = useRelayEnvironment();
  return (
    <QueryRenderer
      environment={environment}
      query={graphql`
        query DevelopmentEnquiryMeQuery {
          me {
            id
            firstName
            lastName
            primaryEmail {
              email
            }
            primaryPhoneNumber {
              number
            }
          }
        }
      `}
      variables={{}}
      render={({
        props: data,
        error,
      }: RenderProps<DevelopmentEnquiryMeQueryResponse>) =>
        (data != null || error != null) && (
          <BaseDevelopmentEnquiry
            me={data?.me}
            development={props.development}
            sentActions={props.sentActions}
            selectedProductId={props.selectedProductId}
            showSubmit={props.showSubmit}
            formRef={props.formRef}
            onProductSelect={props.onProductSelect}
            onSuccessfulSubmit={props.onSuccessfulSubmit}
          />
        )
      }
    />
  );
};
